import React, { ReactElement, useEffect, useState } from 'react';

import { Box, DialogContent, Typography } from '@mui/material';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import {
    clearSite,
    createNewEmptyConstructionSite,
    createNewEmptyWarehouse,
    importClientsForSiteForm,
    importSiteForSiteForm,
    saveSite,
    selectClient,
    selectProject,
} from '../../../../redux/actions/siteActions';
import SiteFormStep from './SiteFormStep';
import ClientFormStep from './ClientFormStep';
import ProjectFormStep from './ProjectFormStep';
import { RootState } from '../../../../redux/reducers';
import { SaveEntityStatus } from '../../../../redux/reducers/common';
import { GoogleMapsFormContainerBox } from '../../../../common/ResponsiveStyle';
import { EntityType, ProjectDto, SiteType } from '../../../../api/ClientApi';
import { useLocation } from 'react-router-dom';
import { WarehouseClient } from '../../../../api/Extensions';

import { FormActions, FormDialogTitle } from '../../../../common/components/form/FormPrimitives';

import { ResponsiveDialog } from '../../../../common/components/mobile/MobilePrimitives';

export interface SiteFormProps {
    open: boolean;
    closeForm(): void;
}

export default function SiteForm(props: SiteFormProps): ReactElement {
    const [isUpdate, setIsUpdate] = useState<boolean>(false);
    const [currentProject, setCurrentProject] = useState<ProjectDto>(new ProjectDto());

    const client = useAppSelector((state: RootState) => state.site.client);
    const createNewClient = useAppSelector((state: RootState) => state.site.createNewClient);
    const selectedClientId = useAppSelector((state: RootState) => state.site.newClientId);

    const project = useAppSelector((state: RootState) => state.site.project);
    const createNewProject = useAppSelector((state: RootState) => state.site.createNewProject);
    const selectedProjectId = useAppSelector((state: RootState) => state.site.newProjectId);

    const site = useAppSelector((state: RootState) => state.site.site);

    const saveStatus = useAppSelector((state: RootState) => state.site.saveStatus);

    const { open, closeForm } = props;

    const dispatch = useAppDispatch();

    const params = useParams();
    const location = useLocation();
    const path = location.pathname;

    const isConstSite = path.indexOf('/constructionSites') !== -1;
    const isWarehouse = path.indexOf('/warehouses') !== -1;
    const isProject = path.indexOf('/projects') !== -1;

    useEffect(() => {
        if (open && !!path) {
            const siteId = params.siteId;
            if (!!siteId && !isNaN(+siteId) && +siteId > 0) {
                dispatch(importSiteForSiteForm(+siteId));
                setIsUpdate(true);
            } else {
                if (isConstSite || isProject) {
                    dispatch(createNewEmptyConstructionSite());
                    setIsUpdate(false);
                } else if (isWarehouse) {
                    dispatch(createNewEmptyWarehouse());
                    setIsUpdate(false);
                }
            }
        }
    }, [params, open, path]);

    useEffect(() => {
        if (open && !!path) {
            const projectId = params.projectId;
            if (!!projectId && !isNaN(+projectId) && +projectId > 0) {
                WarehouseClient.getProjectById(+projectId, true, false, false).then((res) => {
                    setCurrentProject(res);
                    dispatch(selectProject(+projectId));
                    dispatch(selectClient(res.clientId));
                });
            }
        }
    }, [params, open, path]);

    useEffect(() => {
        if (open) {
            dispatch(importClientsForSiteForm());
        }
    }, [dispatch, open]);

    const handleClose = () => {
        dispatch(clearSite());
        closeForm();
    };

    const isClientValid = createNewClient
        ? !!client &&
          !!client?.name &&
          client.name.trim() !== '' &&
          !!client.state &&
          client.state.trim() !== '' &&
          !!client?.shortName &&
          client.shortName.trim() !== '' &&
          (client.entityType === EntityType.Physical || (!!client.taxId && client.taxId.trim() !== '')) &&
          !!client.entityType
        : !!selectedClientId && selectedClientId > 0;

    const isProjectValid = createNewProject
        ? !!project && !!project?.name && project.name.trim() !== ''
        : !!selectedProjectId && selectedProjectId > 0 && !createNewClient;

    const isSiteValid =
        !!site &&
        !!site?.name &&
        site.name.trim() !== '' &&
        !!site.locationInfo &&
        site.locationInfo !== '' &&
        !!site.siteType;

    React.useEffect(() => {
        if (saveStatus === SaveEntityStatus.Saved) {
            handleClose();
        }
    }, [dispatch, saveStatus]);

    const submit = () => {
        dispatch(saveSite());
    };

    const canCreateSite =
        site?.siteType === SiteType.Warehouse
            ? isSiteValid &&
              (!createNewClient || (createNewClient && isClientValid)) &&
              (!createNewProject || (createNewProject && isProjectValid))
            : !!site?.siteType && isSiteValid && isProjectValid && isClientValid;

    const wordOfTheDay = isUpdate ? 'Izmeni' : 'Kreiraj';

    return (
        <ResponsiveDialog scroll="body" maxWidth="md" open={open} onClose={handleClose}>
            <FormDialogTitle>
                {wordOfTheDay} {isConstSite ? 'gradilište' : isWarehouse ? 'magacin' : 'skladište'}
            </FormDialogTitle>
            <DialogContent dividers>
                <GoogleMapsFormContainerBox>
                    {isProject && !!currentProject && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ fontSize: 20 }}>
                                Projekat - {currentProject.name}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: 20 }}>
                                Klijent - {currentProject.client?.shortName ?? currentProject.client?.name ?? '/'}
                            </Typography>
                            <hr />
                        </Box>
                    )}
                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h5" color="primary">
                            Skladište
                        </Typography>
                        <hr />

                        <SiteFormStep chooseType={isUpdate} />
                    </Box>
                    {!isUpdate && !isProject && (
                        <>
                            <Box sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex' }}>
                                    <Typography variant="h5" color="primary">
                                        Klijent/Projekat
                                    </Typography>
                                </Box>
                                <hr />

                                <ClientFormStep
                                    isRequired={!!site?.siteType && site?.siteType !== SiteType.Warehouse}
                                />
                                <Box sx={{ height: 15, width: '100%' }} />
                                <ProjectFormStep
                                    isRequired={!!site?.siteType && site?.siteType !== SiteType.Warehouse}
                                />
                            </Box>
                        </>
                    )}

                    {!canCreateSite && (
                        <Box sx={{ mb: 3 }}>
                            <Typography color="error">*unesite neophodne podatke za skladište</Typography>
                        </Box>
                    )}

                    <FormActions
                        onCancel={handleClose}
                        onSubmit={submit}
                        submitLabel="Sačuvaj"
                        disabled={!canCreateSite || saveStatus === SaveEntityStatus.Pending}
                        pending={saveStatus === SaveEntityStatus.Pending}
                        inGrid={false}
                    />
                </GoogleMapsFormContainerBox>
            </DialogContent>
        </ResponsiveDialog>
    );
}
